import { useState, useEffect } from "react";
import { collection, onSnapshot, updateDoc, doc, deleteDoc, addDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { addLog } from "../../utils/audit";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../services/firebase";

export default function AssetManager() {
  const [assets, setAssets] = useState([]);
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [depreciationAlerts, setDepreciationAlerts] = useState([]);

  const categoryOptions = [
    { value: "ict-equipment", label: "ICT Equipment" },
    { value: "devices", label: "Devices" },
    { value: "laboratory-equipment", label: "Laboratory Equipment" },
  ];

  useEffect(() => {
    const q = collection(db, "assets");
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const assetsData = [];
      const today = new Date();

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const receiveDate = data.receiveDate
          ? new Date(data.receiveDate)
          : new Date(data.createdAt?.toDate?.() || Date.now());
        const depreciationDate = data.depreciationDate
          ? new Date(data.depreciationDate)
          : new Date(receiveDate.getFullYear() + 5, receiveDate.getMonth(), receiveDate.getDate());

        // Auto-update status if depreciated and not already marked
        if (depreciationDate <= today && data.status !== "depreciated") {
          // Fire and forget - don't await to avoid blocking onSnapshot updates
          updateDoc(doc(db, "assets", docSnap.id), {
            status: "depreciated",
            lastUpdated: new Date(),
          }).catch(err => console.error("Failed to auto-update depreciated status:", err));
          data.status = "depreciated"; // Update local data immediately
        }

        // Calculate current book value
        const yearsUsed = Math.max(0, (today - receiveDate) / (365.25 * 24 * 60 * 60 * 1000));
        const bookValue = data.cost && data.salvageValue && data.usefulLife
          ? Math.max(data.salvageValue, data.cost - ((data.cost - data.salvageValue) / data.usefulLife) * yearsUsed)
          : data.cost || 0;

        assetsData.push({
          id: docSnap.id,
          ...data,
          receiveDate,
          depreciationDate,
          bookValue,
          yearsUsed,
        });
      }

      assetsData.sort((a, b) => a.name.localeCompare(b.name));
      setAssets(assetsData);
      checkDepreciationAlerts(assetsData);
    });

    return () => unsubscribe();
  }, []);

  // 🔔 check if any asset is near or past depreciation
  const checkDepreciationAlerts = (assetList) => {
    const today = new Date();
    const thirtyDays = 30 * 24 * 60 * 60 * 1000;
    const alerts = [];

    assetList.forEach((a) => {
      const depDate = new Date(a.depreciationDate);
      const diff = depDate - today;

      if (diff <= 0) {
        alerts.push({
          type: "expired",
          message: `⚠️ ${a.name} has reached its depreciation date (${depDate.toLocaleDateString()}).`,
        });
      } else if (diff <= thirtyDays) {
        alerts.push({
          type: "near",
          message: `🔔 ${a.name} will depreciate on ${depDate.toLocaleDateString()}.`,
        });
      }
    });

    setDepreciationAlerts(alerts);
  };

  const handleStatusChange = async (assetId, newStatus) => {
    setLoading(true);
    try {
      const assetRef = doc(db, "assets", assetId);
      await updateDoc(assetRef, {
        status: newStatus,
        lastUpdated: new Date(),
      });

      const asset = assets.find((a) => a.id === assetId);
      await addLog(user?.uid, `Changed ${asset?.name} status to ${newStatus}`, {
        assetId,
        newStatus,
      });
    } catch (err) {
      alert("Failed to update asset status: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkForDisposal = async (assetId, assetName) => {
    const reason = prompt(`Enter disposal reason for "${assetName}":`);
    if (reason == null || reason.trim() === "") return;

    setDeletingId(assetId);
    try {
      const asset = assets.find(a => a.id === assetId);
      if (!asset) return;

      // Create clean disposal record
      const disposalRecord = {
        id: asset.id,
        name: asset.name || "",
        description: asset.description || "",
        serialNumber: asset.serialNumber || "",
        category: asset.category || "",
        categoryLabel: asset.categoryLabel || "",
        building: asset.building || "",
        room: asset.room || "",
        location: asset.location || "",
        status: asset.status || "available",
        receiveDate: asset.receiveDate || "",
        depreciationDate: asset.depreciationDate || "",
        cost: asset.cost || 0,
        salvageValue: asset.salvageValue || 0,
        usefulLife: asset.usefulLife || 5,
        annualDepreciation: asset.annualDepreciation || 0,
        bookValue: asset.bookValue || 0,
        qrUrl: asset.qrUrl || "",
        qrLink: asset.qrLink || "",
        createdAt: asset.createdAt || new Date(),
        createdBy: asset.createdBy || "",
        disposalReason: reason.trim(),
        disposalDate: new Date().toISOString().split("T")[0],
        disposedBy: user?.uid || "system",
        lastUpdated: new Date(),
      };

      // Move to disposed_assets collection
      await addDoc(collection(db, "disposed_assets"), disposalRecord);

      // Remove from active assets
      await deleteDoc(doc(db, "assets", assetId));

      await addLog(user?.uid, `Marked ${assetName} for disposal: ${reason.trim()}`, { assetId, reason: reason.trim() });
      alert(`✅ Asset "${assetName}" marked for disposal and moved to Disposed Assets.`);
    } catch (err) {
      alert("Failed to mark for disposal: " + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleReevaluateAsset = async (assetId, assetName) => {
    const newUsefulLife = prompt(`Enter new useful life in years for "${assetName}" (current: 5 years):`);
    if (!newUsefulLife || isNaN(newUsefulLife) || newUsefulLife <= 0) return;

    const usefulLife = parseInt(newUsefulLife);
    const receiveDate = assets.find(a => a.id === assetId)?.receiveDate;
    if (!receiveDate) return;

    const newDepreciationDate = new Date(receiveDate);
    newDepreciationDate.setFullYear(receiveDate.getFullYear() + usefulLife);

    setLoading(true);
    try {
      await updateDoc(doc(db, "assets", assetId), {
        usefulLife: usefulLife,
        depreciationDate: newDepreciationDate.toISOString().split("T")[0],
        status: "available", // Reset status
        lastUpdated: new Date(),
      });
      await addLog(user?.uid, `Re-evaluated ${assetName} with ${usefulLife} years useful life`, { assetId, usefulLife });
      alert(`✅ Asset "${assetName}" re-evaluated with ${usefulLife} years useful life.`);
    } catch (err) {
      alert("Failed to re-evaluate asset: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromActive = async (assetId, assetName) => {
    const reason = prompt(`Enter disposal reason for "${assetName}":`);
    if (reason == null || reason.trim() === "") return;

    setDeletingId(assetId);
    try {
      const asset = assets.find(a => a.id === assetId);
      if (!asset) return;

      // Create clean disposal record
      const disposalRecord = {
        id: asset.id,
        name: asset.name || "",
        description: asset.description || "",
        serialNumber: asset.serialNumber || "",
        category: asset.category || "",
        categoryLabel: asset.categoryLabel || "",
        building: asset.building || "",
        room: asset.room || "",
        location: asset.location || "",
        status: asset.status || "available",
        receiveDate: asset.receiveDate || "",
        depreciationDate: asset.depreciationDate || "",
        cost: asset.cost || 0,
        salvageValue: asset.salvageValue || 0,
        usefulLife: asset.usefulLife || 5,
        annualDepreciation: asset.annualDepreciation || 0,
        bookValue: asset.bookValue || 0,
        qrUrl: asset.qrUrl || "",
        qrLink: asset.qrLink || "",
        createdAt: asset.createdAt || new Date(),
        createdBy: asset.createdBy || "",
        disposalReason: reason.trim(),
        disposalDate: new Date().toISOString().split("T")[0],
        disposedBy: user?.uid || "system",
        lastUpdated: new Date(),
      };

      // Move to disposed_assets collection
      await addDoc(collection(db, "disposed_assets"), disposalRecord);

      // Remove from active assets
      await deleteDoc(doc(db, "assets", assetId));

      await addLog(user?.uid, `Removed ${assetName} from active list: ${reason.trim()}`, { assetId, reason: reason.trim() });
      alert(`✅ Asset "${assetName}" removed from active list.`);
    } catch (err) {
      alert("Failed to remove from active list: " + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteAsset = async (assetId, assetName) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${assetName}"? This action cannot be undone.`))
      return;

    setDeletingId(assetId);
    try {
      await deleteDoc(doc(db, "assets", assetId));
      await addLog(user?.uid, `Permanently deleted asset: ${assetName}`, { assetId, assetName });
      alert(`✅ Asset "${assetName}" permanently deleted.`);
    } catch (err) {
      alert("Failed to delete asset: " + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-800 border-green-200";
      case "in-use":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "maintenance":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "missing":
        return "bg-red-100 text-red-800 border-red-200";
      case "depreciated":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const statusOptions = [
    { value: "available", label: "Available" },
    { value: "in-use", label: "In Use" },
    { value: "maintenance", label: "Under Maintenance" },
    { value: "missing", label: "Missing" },
    { value: "depreciated", label: "Depreciated" },
  ];

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch =
      asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serialNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === "all" || asset.status === statusFilter;
    const matchesCategory = categoryFilter === "all" || asset.category === categoryFilter;

    return matchesSearch && matchesStatus && matchesCategory;
  });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Asset Status Manager</h2>
          <p className="text-gray-600 mt-1">Manage assets with depreciation alerts.</p>
        </div>
        <div className="text-sm text-gray-500">
          Showing: {filteredAssets.length} of {assets.length}
        </div>
      </div>

      {/* 🔔 Depreciation Alerts */}
      {depreciationAlerts.length > 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-300 text-yellow-900 p-4 rounded-lg">
          <h3 className="font-semibold mb-2">Depreciation Alerts</h3>
          <ul className="list-disc pl-5 space-y-1">
            {depreciationAlerts.map((alert, idx) => (
              <li key={idx}>{alert.message}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or serial..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All</option>
              {statusOptions.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="all">All</option>
              {categoryOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Asset List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredAssets.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No assets found</h3>
          </div>
        ) : (
          filteredAssets.map((asset) => {
            const isNear =
              new Date(asset.depreciationDate) - new Date() <= 30 * 24 * 60 * 60 * 1000 &&
              new Date(asset.depreciationDate) > new Date();
            const isExpired = new Date(asset.depreciationDate) <= new Date();

            return (
              <div
                key={asset.id}
                className={`bg-white rounded-2xl shadow-lg border p-6 ${
                  isExpired ? "border-red-400" : isNear ? "border-yellow-400" : "border-gray-200"
                }`}
              >
                <div className="flex flex-col lg:flex-row justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900">{asset.name}</h3>
                    <p className="text-sm text-gray-600">{asset.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3 text-sm text-gray-700">
                      <p>
                        <span className="font-medium">Receive Date:</span>{" "}
                        {asset.receiveDate.toLocaleDateString()}
                      </p>
                      <p>
                        <span className="font-medium">Book Value:</span>{" "}
                        ₱{asset.bookValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                      <p>
                        <span className="font-medium">Depreciation Date:</span>{" "}
                        {asset.depreciationDate.toLocaleDateString()}
                      </p>
                      <p>
                        <span className="font-medium">Status:</span>{" "}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            asset.status
                          )}`}
                        >
                          {asset.status}
                        </span>
                      </p>
                    </div>

                    {isExpired && (
                      <p className="text-red-600 mt-2 font-medium">⚠️ This asset is depreciated.</p>
                    )}
                    {isNear && !isExpired && (
                      <p className="text-yellow-600 mt-2 font-medium">
                        🔔 Depreciation in less than 30 days.
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-4 lg:mt-0 lg:w-64 flex flex-col space-y-2">
                    <select
                      value={asset.status || ""}
                      onChange={(e) => handleStatusChange(asset.id, e.target.value)}
                      disabled={loading}
                      className="px-3 py-2 border rounded-md text-sm"
                    >
                      {statusOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>

                    <div className="grid grid-cols-1 gap-2">
                      {asset.status === "depreciated" && (
                        <>
                          <button
                            onClick={() => handleReevaluateAsset(asset.id, asset.name)}
                            disabled={loading}
                            className="bg-blue-600 text-white py-1.5 px-3 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
                          >
                            Re-evaluate Asset
                          </button>
                          <button
                            onClick={() => handleMarkForDisposal(asset.id, asset.name)}
                            disabled={deletingId === asset.id}
                            className="bg-purple-600 text-white py-1.5 px-3 rounded-md hover:bg-purple-700 disabled:opacity-50 text-sm"
                          >
                            {deletingId === asset.id ? "Disposing..." : "Mark for Disposal"}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleRemoveFromActive(asset.id, asset.name)}
                        disabled={deletingId === asset.id}
                        className="bg-orange-600 text-white py-1.5 px-3 rounded-md hover:bg-orange-700 disabled:opacity-50 text-sm"
                      >
                        {deletingId === asset.id ? "Removing..." : "Remove from Active"}
                      </button>
                      <button
                        onClick={() => handleDeleteAsset(asset.id, asset.name)}
                        disabled={deletingId === asset.id}
                        className="bg-red-600 text-white py-1.5 px-3 rounded-md hover:bg-red-700 disabled:opacity-50 text-sm"
                      >
                        {deletingId === asset.id ? "Deleting..." : "Delete Asset"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
